import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type {
  MemberView,
  PageResponse,
  PermissionBundle,
  RoleAssignmentView,
  SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import {
  assignableBundlesForMember,
  assignmentStatus,
  bundleLabelKeys,
  endOfDayIso,
  memberStatusLabelKey,
  shortIdentifier,
  statusLabelKeys,
} from './roles';

export interface RolesPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
  refreshAccount: () => Promise<void>;
}

type MemberTransition = 'suspend' | 'reactivate' | 'remove';
type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

export function RolesPanel({
  locale,
  organizationId,
  csrfToken,
  refreshAccount,
}: RolesPanelProps): React.JSX.Element {
  const [members, setMembers] = useState<MemberView[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const base = `/api/v1/organizations/${organizationId}`;

  const load = useCallback(
    async (cursor: string | null) => {
      try {
        const query = cursor === null ? '' : `?cursor=${encodeURIComponent(cursor)}`;
        const page = await apiRequest<PageResponse<MemberView>>(`${base}/members${query}`);
        setMembers((current) =>
          cursor === null ? page.items : [...(current ?? []), ...page.items],
        );
        setNextCursor(page.nextCursor);
      } catch (error) {
        setErrorKey(errorKeyOf(error));
      }
    },
    [base],
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  async function change(work: () => Promise<unknown>, selfAssignment = false): Promise<boolean> {
    setErrorKey(null);
    try {
      await work();
      if (selfAssignment) {
        // Assignment ends our sessions. Reload the account to return to sign-in, rather than
        // loading members with an expired session and presenting a successful save as an error.
        await refreshAccount();
        return true;
      }
      await load(null);
      return true;
    } catch (error) {
      setErrorKey(errorKeyOf(error));
      return false;
    }
  }

  function changeStatus(member: MemberView, transition: MemberTransition): Promise<boolean> {
    return change(() =>
      apiRequest(`${base}/members/${member.organizationUserId}/${transition}`, {
        method: 'POST',
        body: {},
        csrfToken,
        ifMatchVersion: member.version,
      }),
    );
  }

  return (
    <Box component="section" aria-labelledby="roles-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h2" id="roles-title" gutterBottom>
        {translate(locale, 'roles.title')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {members === null && errorKey === null && (
        <Typography>{translate(locale, 'roles.loading')}</Typography>
      )}
      {members?.length === 0 && <Typography>{translate(locale, 'roles.empty')}</Typography>}
      <Stack spacing={2}>
        {members?.map((member) => (
          <RoleMemberCard
            key={member.organizationUserId}
            locale={locale}
            member={member}
            isSelf={member.isSelf}
            onAssign={(bundle, validUntil) =>
              change(
                () =>
                  apiRequest(`${base}/members/${member.organizationUserId}/roles`, {
                    method: 'POST',
                    body: { permissionBundle: bundle, validUntil },
                    csrfToken,
                  }),
                member.isSelf,
              )
            }
            onChangeStatus={(transition) => changeStatus(member, transition)}
            onUpdateValidity={(assignment, validUntil) =>
              change(() =>
                apiRequest(`${base}/members/${member.organizationUserId}/roles/${assignment.id}`, {
                  method: 'PATCH',
                  body: { validUntil },
                  csrfToken,
                  ifMatchVersion: assignment.version,
                }),
              )
            }
            onRevoke={(assignment) =>
              change(() =>
                apiRequest(
                  `${base}/members/${member.organizationUserId}/roles/${assignment.id}/revoke`,
                  { method: 'POST', body: {}, csrfToken, ifMatchVersion: assignment.version },
                ),
              )
            }
          />
        ))}
      </Stack>
      {nextCursor !== null && (
        <Button sx={{ mt: 2 }} onClick={() => void load(nextCursor)}>
          {translate(locale, 'roles.loadMore')}
        </Button>
      )}
    </Box>
  );
}

interface MemberCardProps {
  locale: SupportedLocale;
  member: MemberView;
  isSelf: boolean;
  onAssign: (bundle: PermissionBundle, validUntil: string | null) => Promise<boolean>;
  onRevoke: (assignment: RoleAssignmentView) => Promise<boolean>;
  onUpdateValidity: (assignment: RoleAssignmentView, validUntil: string | null) => Promise<boolean>;
  onChangeStatus: (transition: MemberTransition) => Promise<boolean>;
}

export function RoleMemberCard({
  locale,
  member,
  isSelf,
  onAssign,
  onRevoke,
  onChangeStatus,
  onUpdateValidity,
}: MemberCardProps): React.JSX.Element {
  const [bundle, setBundle] = useState<PermissionBundle>('coach');
  const [endDate, setEndDate] = useState('');
  const [confirming, setConfirming] = useState<string | null>(null);
  const [confirmingSuspension, setConfirmingSuspension] = useState(false);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const now = new Date();
  const availableBundles = assignableBundlesForMember(member, now);
  const heading = member.displayName ?? member.email ?? translate(locale, 'roles.member.unnamed');
  const secondaryEmail = member.displayName === null ? null : member.email;

  return (
    <Box
      component="article"
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, padding: 2 }}
      aria-label={heading}
    >
      <Typography component="h3" variant="subtitle1" gutterBottom>
        {heading} {isSelf && translate(locale, 'roles.you')}{' '}
        <Chip
          size="small"
          component="span"
          label={translate(locale, memberStatusLabelKey(member.status))}
          color={member.status === 'active' ? 'success' : 'warning'}
        />
      </Typography>
      {secondaryEmail !== null && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {secondaryEmail}
        </Typography>
      )}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {member.assignments.map((assignment) => {
          const status = assignmentStatus(assignment, now);
          const scope =
            assignment.departmentId === null
              ? translate(locale, 'roles.scope.organization')
              : `${translate(locale, 'roles.department')} …${shortIdentifier(assignment.departmentId)}`;
          return (
            <Box
              component="li"
              key={assignment.id}
              sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}
            >
              <Typography component="span">
                {translate(locale, bundleLabelKeys[assignment.permissionBundle])} · {scope}
                {assignment.validUntil !== null &&
                  ` · ${translate(locale, 'roles.until')} ${assignment.validUntil.slice(0, 10)}`}
              </Typography>
              <Chip
                size="small"
                label={translate(locale, statusLabelKeys[status])}
                color={status === 'active' ? 'success' : 'default'}
              />
              {!isSelf && member.status === 'active' && assignment.revokedAt === null && (
                <RoleValidityEditor
                  locale={locale}
                  assignment={assignment}
                  onSave={onUpdateValidity}
                />
              )}
              {status === 'active' &&
                (confirming === assignment.id ? (
                  <Button
                    size="small"
                    color="error"
                    variant="contained"
                    onClick={() => {
                      setConfirming(null);
                      void onRevoke(assignment);
                    }}
                  >
                    {translate(locale, 'roles.revoke.confirm')}
                  </Button>
                ) : (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      setConfirming(assignment.id);
                    }}
                  >
                    {translate(locale, 'roles.revoke')}
                  </Button>
                ))}
            </Box>
          );
        })}
      </Stack>
      {!isSelf && (member.status === 'active' || member.status === 'suspended') && (
        <Box sx={{ mt: 2 }}>
          {member.status === 'suspended' ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                void onChangeStatus('reactivate');
              }}
            >
              {translate(locale, 'roles.member.reactivate')}
            </Button>
          ) : confirmingSuspension ? (
            <Button
              size="small"
              color="error"
              variant="contained"
              onClick={() => {
                setConfirmingSuspension(false);
                void onChangeStatus('suspend');
              }}
            >
              {translate(locale, 'roles.member.suspend.confirm')}
            </Button>
          ) : (
            <Button
              size="small"
              color="error"
              onClick={() => {
                setConfirmingSuspension(true);
              }}
            >
              {translate(locale, 'roles.member.suspend')}
            </Button>
          )}{' '}
          {confirmingRemoval ? (
            <>
              <Alert severity="warning" sx={{ my: 1 }}>
                {translate(locale, 'roles.member.remove.warning')}
              </Alert>
              <Button
                size="small"
                color="error"
                variant="contained"
                onClick={() => {
                  setConfirmingRemoval(false);
                  void onChangeStatus('remove');
                }}
              >
                {translate(locale, 'roles.member.remove.confirm')}
              </Button>
            </>
          ) : (
            <Button
              size="small"
              color="error"
              onClick={() => {
                setConfirmingRemoval(true);
              }}
            >
              {translate(locale, 'roles.member.remove')}
            </Button>
          )}
        </Box>
      )}
      {availableBundles.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {isSelf && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {translate(locale, 'roles.assign.selfHelp')}
            </Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
            <TextField
              select
              size="small"
              label={translate(locale, 'roles.assign.bundle')}
              disabled={assigning}
              value={bundle}
              onChange={(event) => {
                setBundle(event.target.value as PermissionBundle);
              }}
              sx={{ minWidth: 220 }}
            >
              {availableBundles.map((option) => (
                <MenuItem key={option} value={option}>
                  {translate(locale, bundleLabelKeys[option])}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              type="date"
              label={translate(locale, 'roles.assign.until')}
              disabled={assigning}
              value={endDate}
              onChange={(event) => {
                setEndDate(event.target.value);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Button
              variant="contained"
              disabled={assigning}
              onClick={() => {
                const validUntil = endDate === '' ? null : endOfDayIso(endDate);
                // An end date that cannot be read must not silently become a role without an end.
                if (endDate !== '' && validUntil === null) return;
                setAssigning(true);
                void onAssign(bundle, validUntil)
                  .then((added) => {
                    if (added) setEndDate('');
                  })
                  .finally(() => {
                    setAssigning(false);
                  });
              }}
            >
              {translate(locale, 'roles.assign.submit')}
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}

function RoleValidityEditor({
  locale,
  assignment,
  onSave,
}: {
  locale: SupportedLocale;
  assignment: RoleAssignmentView;
  onSave: (assignment: RoleAssignmentView, validUntil: string | null) => Promise<boolean>;
}): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [endDate, setEndDate] = useState('');
  const [saving, setSaving] = useState(false);
  if (!editing)
    return (
      <Button
        size="small"
        onClick={() => {
          const end = assignment.validUntil === null ? null : new Date(assignment.validUntil);
          setEndDate(
            end === null
              ? ''
              : `${String(end.getFullYear())}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`,
          );
          setEditing(true);
        }}
      >
        {translate(locale, 'roles.validity.edit')}
      </Button>
    );
  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      <Typography variant="body2">{translate(locale, 'roles.validity.help')}</Typography>
      <TextField
        type="date"
        size="small"
        label={translate(locale, 'roles.assign.until')}
        value={endDate}
        disabled={saving}
        onChange={(event) => {
          setEndDate(event.target.value);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <Stack direction="row" spacing={1}>
        <Button
          disabled={saving}
          onClick={() => {
            const validUntil = endDate === '' ? null : endOfDayIso(endDate);
            if (endDate !== '' && validUntil === null) return;
            setSaving(true);
            void onSave(assignment, validUntil).then((saved) => {
              setSaving(false);
              if (saved) setEditing(false);
            });
          }}
        >
          {translate(locale, 'roles.validity.save')}
        </Button>
        <Button
          disabled={saving}
          onClick={() => {
            setEditing(false);
          }}
        >
          {translate(locale, 'roles.validity.cancel')}
        </Button>
      </Stack>
    </Stack>
  );
}
